import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { CreditCard, ShieldAlert, Loader2 } from "lucide-react";

/**
 * Unified payment button.
 * Reads gateway config from backend (synced with /app/admin → Payments).
 *
 * Props:
 *   amount: int (rupees/dollars — we convert to lowest unit)
 *   currency: "INR" | "USD" | ...
 *   country: "IN" | "US" | ...
 *   purpose: "donation" | "store_order" | "vet_booking" | "hostel_booking"
 *   referenceId?: string
 *   label?: string
 *   onSuccess?(intent): void
 *   onError?(err): void
 */
export default function PayButton({ amount, currency = "INR", country = "IN", purpose, referenceId, label, onSuccess, onError, className = "", dataTestid = "pay-button" }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/public-config`, { params: { country } }).then(r => setCfg(r.data)).catch(() => setCfg({ gateway: "mock", configured: false, currency, country }));
  }, [country, currency]);

  const symbol = (cfg?.currency || currency) === "INR" ? "₹" : (cfg?.currency || currency) === "USD" ? "$" : "";

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const lowest = Math.round(Number(amount) * 100);
      const intentRes = await axios.post(`${API}/payments/create-intent`, { purpose, amount: lowest, currency: cfg?.currency || currency, country, reference_id: referenceId });
      const intent = intentRes.data;

      if (intent.gateway === "razorpay" && window.Razorpay) {
        await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: intent.publishable_key,
            amount: intent.amount,
            currency: intent.currency || "INR",
            name: "PetBond",
            description: `${purpose}`,
            order_id: intent.gateway_order_id,
            prefill: { name: user?.name, email: user?.email },
            theme: { color: "#FF6B35" },
            handler: async (resp) => {
              await axios.post(`${API}/payments/confirm`, { intent_id: intent.intent_id, gateway_payment_id: resp.razorpay_payment_id, success: true });
              onSuccess && onSuccess({ ...intent, status: "succeeded", gateway_payment_id: resp.razorpay_payment_id });
              resolve();
            },
            modal: { ondismiss: () => { reject(new Error("dismissed")); } },
          });
          rzp.open();
        }).catch((e) => { if (e.message !== "dismissed" && onError) onError(e); });
      } else if (intent.gateway === "stripe" && intent.checkout_url) {
        try { sessionStorage.setItem(`petbond_intent_${intent.intent_id}`, JSON.stringify({ purpose, referenceId })); } catch {}
        window.location.href = intent.checkout_url;
      } else {
        // MOCK fallback — admin hasn't configured a live gateway
        await axios.post(`${API}/payments/confirm`, { intent_id: intent.intent_id, success: true, gateway_payment_id: "demo_" + Date.now() });
        onSuccess && onSuccess({ ...intent, status: "succeeded", gateway_payment_id: "demo" });
      }
    } catch (e) {
      onError && onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={dataTestid + "-wrap"}>
      <button
        data-testid={dataTestid}
        onClick={pay}
        disabled={busy || !cfg}
        className={`w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover disabled:opacity-60 inline-flex items-center justify-center gap-2 ${className}`}
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Processing…</> : <><CreditCard className="w-4 h-4"/> {label || `Pay ${symbol}${amount}`}</>}
      </button>
      {cfg && !cfg.configured && (
        <div className="mt-2 text-xs text-ink-soft flex items-start gap-1.5 bg-sunshine/20 border border-sunshine rounded-2xl px-3 py-2" data-testid="pay-demo-badge">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>Demo mode — no live payment gateway is enabled for {cfg.country}. Admin can enable Razorpay/Stripe at <strong>/app/admin → Payments</strong>.</span>
        </div>
      )}
      {cfg?.configured && (
        <div className="mt-2 text-[11px] text-ink-soft text-center" data-testid="pay-gateway-badge">
          🔒 Secure payments by <strong className="capitalize">{cfg.gateway}</strong>
        </div>
      )}
    </div>
  );
}
